import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../services/api';
import {
  jobVisibilityService,
  type JobVisibilityConfigDto,
  type AvailableJobType,
} from '../../services/jobVisibilityService';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';

interface Role {
  id: string;
  code: string;
  name: string;
  color: string;
  labelColor: string;
}

export const JobVisibilityConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<JobVisibilityConfigDto[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalConfigId, setModalConfigId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [availableTypes, setAvailableTypes] = useState<AvailableJobType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [createJobTypeName, setCreateJobTypeName] = useState('');
  const [createIsVisible, setCreateIsVisible] = useState(true);
  const [createRoleIds, setCreateRoleIds] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [configsRes, rolesRes] = await Promise.all([
        jobVisibilityService.getAll(),
        api.get<Role[]>('/roles'),
      ]);
      setConfigs(configsRes);
      setRoles(rolesRes.data);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Erro ao carregar dados.';
      addToast(msg, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleVisible = async (config: JobVisibilityConfigDto) => {
    try {
      const updated = await jobVisibilityService.update(config.id, {
        isVisible: !config.isVisible,
      });
      setConfigs(prev => prev.map(c => (c.id === config.id ? updated : c)));
      addToast(updated.isVisible ? 'Job visivel.' : 'Job ocultado.', 'success');
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Erro ao atualizar.';
      addToast(msg, 'error');
    }
  };

  const handleToggleRole = async (configId: string, roleId: string) => {
    try {
      const updated = await jobVisibilityService.toggleRole(configId, roleId);
      setConfigs(prev => prev.map(c => (c.id === configId ? updated : c)));
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Erro ao atualizar perfil.';
      addToast(msg, 'error');
    }
  };

  const handleOpenCreateModal = async () => {
    setShowCreateModal(true);
    setCreateJobTypeName('');
    setCreateIsVisible(true);
    setCreateRoleIds([]);
    setIsLoadingTypes(true);
    try {
      const types = await jobVisibilityService.getAvailableTypes();
      setAvailableTypes(types);
    } catch {
      setAvailableTypes([]);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const handleCreate = async () => {
    if (!createJobTypeName.trim()) {
      addToast('Selecione um job.', 'error');
      return;
    }
    try {
      setIsCreating(true);
      const created = await jobVisibilityService.create({
        jobTypeName: createJobTypeName.trim(),
        isVisible: createIsVisible,
        roleIds: createRoleIds,
      });
      setConfigs(prev => [...prev, created]);
      setShowCreateModal(false);
      setCreateJobTypeName('');
      setCreateIsVisible(true);
      setCreateRoleIds([]);
      addToast('Job criado com sucesso!', 'success');
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Erro ao criar job.';
      addToast(msg, 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await jobVisibilityService.delete(id);
      setConfigs(prev => prev.filter(c => c.id !== id));
      setDeleteConfirmId(null);
      addToast('Job removido com sucesso!', 'success');
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Erro ao remover job.';
      addToast(msg, 'error');
    }
  };

  const modalConfig = configs.find(c => c.id === modalConfigId);
  const deleteConfig = configs.find(c => c.id === deleteConfirmId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuração de Jobs a Monitorar</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie a visibilidade e o nome de exibição dos jobs por perfil.
          </p>
        </div>
        <Button variant="primary" onClick={handleOpenCreateModal}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Job a Monitorar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nome Técnico
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nome Exibido
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Visível
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Perfis
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {configs.map(config => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-400 font-mono">
                    {config.jobTypeName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {config.displayName}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleToggleVisible(config)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        config.isVisible ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          config.isVisible ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {config.roles.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">Todos os perfis</span>
                      ) : (
                        config.roles.map(r => (
                          <span
                            key={r.roleId}
                            className={`${r.roleColor || 'bg-gray-100'} ${r.roleLabelColor || 'text-gray-700'} text-xs font-medium px-2 py-0.5 rounded`}
                          >
                            {r.roleName}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setModalConfigId(config.id)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setDeleteConfirmId(config.id)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Roles */}
      {modalConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModalConfigId(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Gerenciar Perfis</h2>
                  <p className="text-sm text-gray-500 mt-1">{modalConfig.displayName}</p>
                </div>
                <button
                  onClick={() => setModalConfigId(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {modalConfig.roles.length === 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700">
                    Nenhum perfil selecionado — este job esta visível para todos os perfis.
                  </p>
                </div>
              )}

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {roles.map(role => {
                  const isLinked = modalConfig.roles.some(r => r.roleId === role.id);
                  return (
                    <div
                      key={role.id}
                      className="flex items-center justify-between p-3 rounded-md border border-gray-200 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`${role.color || 'bg-gray-100'} ${role.labelColor || 'text-gray-700'} text-xs font-medium px-2.5 py-0.5 rounded`}>
                          {role.name}
                        </span>
                      </div>
                      <Button
                        variant={isLinked ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => handleToggleRole(modalConfig.id, role.id)}
                      >
                        {isLinked ? 'Desativar' : 'Ativar'}
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end">
                <Button variant="secondary" onClick={() => setModalConfigId(null)}>
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Criacao */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Novo Job a Monitorar</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job *</label>
                  {isLoadingTypes ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                      Carregando tipos...
                    </div>
                  ) : availableTypes.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-2">Todos os jobs já estão configurados.</p>
                  ) : (
                    <select
                      value={createJobTypeName}
                      onChange={e => setCreateJobTypeName(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Selecione um job...</option>
                      {availableTypes.map(type => (
                        <option key={type.typeName} value={type.typeName}>{type.displayName}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">Visível</label>
                  <button
                    type="button"
                    onClick={() => setCreateIsVisible(v => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      createIsVisible ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        createIsVisible ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Perfis vinculados</label>
                  {createRoleIds.length === 0 && (
                    <p className="text-xs text-gray-400 italic mb-2">Nenhum perfil selecionado — visível para todos.</p>
                  )}
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {roles.map(role => {
                      const isSelected = createRoleIds.includes(role.id);
                      return (
                        <label
                          key={role.id}
                          className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                            isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              setCreateRoleIds(prev =>
                                isSelected ? prev.filter(id => id !== role.id) : [...prev, role.id]
                              )
                            }
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className={`${role.color || 'bg-gray-100'} ${role.labelColor || 'text-gray-700'} text-xs font-medium px-2 py-0.5 rounded`}>
                            {role.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleCreate} disabled={isCreating}>
                  {isCreating ? 'Criando...' : 'Criar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmacao de Exclusao */}
      {deleteConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Confirmar exclusão</h2>
              <p className="text-sm text-gray-600">
                Tem certeza que deseja remover <strong>{deleteConfig.displayName}</strong>? Esta ação não pode ser desfeita.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
                  Cancelar
                </Button>
                <Button variant="danger" onClick={() => handleDelete(deleteConfig.id)}>
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobVisibilityConfigPage;
